/*
PURPOSE: User profile page that allows any authenticated user to view any other user's profile

USE CASES:
- Display user activity feed showing ContentItems they have engaged with via comments
- Show user's coral collections using existing coral visibility model
- Provide tabbed interface for activity and coral content
- Enable cross-user profile viewing - any authenticated user can view any other user's profile
- Support navigation to user profiles from usernames throughout the app

DOMAIN BOUNDARIES:
- Responsible for user profile page layout and data orchestration
- Uses existing ContentFeedCard components for activity display
- Uses existing coral components for coral collection display
- Does NOT handle user authentication or authorization (handled by routes)

ASSUMPTIONS:
- User making the request is authenticated with valid credentials
- Activity feed is based on comments the target user has made on ContentItems
- Any authenticated user can view any other user's activity (public profiles)
- ContentItems the user commented on should be displayed using existing ContentFeedCard components
- Existing comment functionality works normally within ContentFeedCard components
*/

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getUserActivity, getUserCorals, getUserCoral, getUserFromAPI } from '../../../api/api';
import { useAuth } from '../../../hooks/useAuth';
import { useCopy } from '../../../hooks/useCopy';
import { useDancefloorTheme } from '../../../hooks/useDancefloorTheme';
import MetaHead from '../../../components/MetaHead/MetaHead';
import ContentFeed from '../../../components/ContentFeed/ContentFeed';
import { CoralGrid } from '../../../components/CoralGrid';
import AlignmentsGrid from '../../../components/AlignmentsGrid/AlignmentsGrid';
import TabDescriptor from '../../../components/TabDescriptor/TabDescriptor';
import GesturesTab from '../../../components/GesturesTab/GesturesTab';
import { User, FilteredClientCoral } from 'shared/types/platformTypes';
import { ContentFeed as ContentFeedType, OriginLocation } from 'shared/types/contentTypes';
import { getUserColors, getUserInitials } from '../../../utils/colorUtils';
import AlignButton from '../../../components/AlignButton/AlignButton';

const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser2: currentUser } = useAuth();
  const copy = useCopy();

  // Get the user's gesture theme for dynamic dancefloor labeling
  const userTheme = useDancefloorTheme({ userId });

  // State for user data and UI
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Determine initial tab from URL parameters
  const tabFromURL = searchParams.get('view') || 'activity';
  const [activeTab, setActiveTab] = useState<string>(tabFromURL);

  // Refs for tab buttons to enable scroll-into-view
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Scroll active tab into view on mount and when activeTab changes
  useEffect(() => {
    // Small delay to ensure DOM is ready after render
    const timeoutId = setTimeout(() => {
      const activeButton = tabRefs.current[activeTab];
      if (activeButton) {
        activeButton.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [activeTab]);

  // Activity data state
  const [activityFeed, setActivityFeed] = useState<ContentFeedType>({ items: [] });
  const [isActivityLoading, setIsActivityLoading] = useState(true);
  
  // Coral data state
  const [detailedCorals, setDetailedCorals] = useState<FilteredClientCoral[]>([]);
  const [isCoralsLoading, setIsCoralsLoading] = useState(true);
  
  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        const userData = await getUserFromAPI(userId);
        
        if (userData) {
          setUser(userData);
        } else {
          throw new Error('User not found');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch user data'));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserData();
  }, [userId]);
  
  // Fetch activity data
  useEffect(() => {
    const fetchActivityData = async () => {
      if (!userId) return;
      
      try {
        setIsActivityLoading(true);
        const response = await getUserActivity(userId);
        setActivityFeed(response);
      } catch (err) {
        console.error('Error fetching activity data:', err);
        setActivityFeed({ items: [] });
      } finally {
        setIsActivityLoading(false);
      }
    };
    
    fetchActivityData();
  }, [userId]);
  
  // Fetch coral data
  useEffect(() => {
    const fetchCoralData = async () => {
      if (!userId) return;
      
      try {
        setIsCoralsLoading(true);
        
        // First, get the basic coral list
        const basicCorals = await getUserCorals(userId);
        
        // Then, fetch detailed coral data for each coral
        if (basicCorals && basicCorals.length > 0) {
          const detailedCoralPromises = basicCorals.map(coral => 
            getUserCoral(userId, coral.guid)
          );
          
          const detailedCoralResults = await Promise.allSettled(detailedCoralPromises);
          const successfulCorals = detailedCoralResults
            .filter((result): result is PromiseFulfilledResult<FilteredClientCoral> => 
              result.status === 'fulfilled'
            )
            .map(result => result.value);
          
          setDetailedCorals(successfulCorals);
        } else {
          setDetailedCorals([]);
        }
      } catch (err) {
        console.error('Error fetching coral data:', err);
        setDetailedCorals([]);
      } finally {
        setIsCoralsLoading(false);
      }
    };
    
    fetchCoralData();
  }, [userId]);
  
  // Handle tab change and update URL
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId === 'activity') {
      searchParams.delete('view');
    } else {
      searchParams.set('view', tabId);
    }
    setSearchParams(searchParams);
  };
  
  // Loading state
  // Get color schemes
  const userColors = user ? getUserColors(user.displayName || user.firstName || user.email || 'User') : null;
  const userInitials = user ? getUserInitials(user.displayName || user.firstName || user.email || 'User') : 'U';

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  // Error state
  if (error || !user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p>{error?.message || 'User not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-2 text-red-700 underline"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <MetaHead
        title={`${user.displayName || 'User'} | Coral`}
        description={`View ${user.displayName || 'user'}&apos;s profile and activity on Coral.`}
      />
      
      <div className="min-h-screen bg-[#faf9f7] pt-4 md:pt-8">
        {/* User Profile Header */}
        <div className="relative pb-6 ml-4 md:ml-8">
          <div className="relative bg-gradient-to-br from-blue-200/40 via-indigo-100/20 to-blue-200/40 overflow-hidden rounded-l-[5rem] shadow-lg">
            {/* Diagonal stripes on the right - using user's profile color */}
            {userColors && (
              <div
                className="absolute inset-0 opacity-[0.08]"
                style={{
                  backgroundImage: `repeating-linear-gradient(
                    -45deg,
                    transparent,
                    transparent 20px,
                    ${userColors.primary} 20px,
                    ${userColors.primary} 22px
                  )`,
                  maskImage: 'linear-gradient(to left, black 0%, black 15%, transparent 50%)',
                  WebkitMaskImage: 'linear-gradient(to left, black 0%, black 15%, transparent 50%)'
                }}
              />
            )}
            
            {/* Content */}
            <div className="relative px-6 py-10 md:px-8 md:py-12">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
                {/* User Avatar */}
                <div className="relative flex-shrink-0 group">
                  {/* Animated ring */}
                  <div 
                    className="absolute inset-0 rounded-full animate-spin-slow opacity-75 blur-md"
                    style={{
                      background: `linear-gradient(45deg, ${userColors?.primary}, ${userColors?.secondary})`
                    }}
                  />
                  
                  {/* Avatar container */}
                  <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden shadow-2xl ring-4 ring-white bg-white">
                    <div 
                      className="w-full h-full flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, ${userColors?.primary}, ${userColors?.secondary})`
                      }}
                    >
                      <span className="text-white text-6xl md:text-7xl font-bold">
                        {userInitials}
                      </span>
                    </div>
                  </div>
                </div>
                  
                {/* User Info */}
                <div className="flex-1 text-center md:text-left">
                  <div className="space-y-4">
                    {/* Display Name */}
                    <div>
                      <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 relative inline-block">
                        {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User'}
                        {/* Decorative underline */}
                        <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-coral-pink via-purple-500 to-coral-pink rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                      </h1>
                    </div>
                    
                    {/* User Type Badge and Actions */}
                    <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                      <div className="flex items-center gap-2 text-gray-700 bg-white/70 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">
                        <span>Contributor</span>
                      </div>
                      {currentUser?.userId === userId && (
                        <div 
                          className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium border border-emerald-500/20"
                          style={{ color: userColors?.primary }}
                        >
                          <span>You</span>
                        </div>
                      )}
                      {/* Align button - only show for other users */}
                      {currentUser?.userId && userId && currentUser.userId !== userId && (
                        <AlignButton
                          targetUserId={userId}
                          className="bg-white/80 backdrop-blur-sm"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation - Full width without padding */}
        <div className="flex justify-center mb-3 overflow-x-auto no-scrollbar pt-4">
          <nav className="inline-flex bg-transparent p-1 rounded-full flex-nowrap">
            <button
              ref={(el) => { tabRefs.current['activity'] = el; }}
              onClick={() => handleTabChange('activity')}
              className={`relative px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap ${
                activeTab === 'activity'
                  ? 'bg-[#faf9f7] text-blue-500 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Activity
            </button>
            <button
              ref={(el) => { tabRefs.current['gestures'] = el; }}
              onClick={() => handleTabChange('gestures')}
              className={`relative px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap ${
                activeTab === 'gestures'
                  ? 'bg-[#faf9f7] text-blue-500 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {userTheme.spaceName}
            </button>
            <button
              ref={(el) => { tabRefs.current['alignments'] = el; }}
              onClick={() => handleTabChange('alignments')}
              className={`relative px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap ${
                activeTab === 'alignments'
                  ? 'bg-[#faf9f7] text-blue-500 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Alignments
            </button>
            <button
              ref={(el) => { tabRefs.current['coral'] = el; }}
              onClick={() => handleTabChange('coral')}
              className={`relative px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap ${
                activeTab === 'coral'
                  ? 'bg-[#faf9f7] text-blue-500 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Coral
            </button>
          </nav>
        </div>

        {/* Main content container */}
        <div className="container mx-auto px-4">
          {/* Tab Content */}
          {activeTab === 'activity' && (
            <>
              <TabDescriptor
                text={copy.platformPages.userProfile.tabs.activity.descriptor(user.displayName || user.firstName || 'this user')}
              />
              <ContentFeed
                items={activityFeed.items}
                isLoading={isActivityLoading}
                emptyMessage={`No recent activity from ${user.displayName || 'this user'}.`}
                className="space-y-4"
              />
            </>
          )}

          {activeTab === 'gestures' && userId && (
            <>
              <TabDescriptor
                text={currentUser?.userId === userId
                  ? userTheme.descriptor.viewingOwn
                  : userTheme.descriptor.viewingOthers(user.displayName || user.firstName || 'this user')
                }
              />
              <GesturesTab
                targetUserId={userId}
                targetDisplayName={user.displayName || user.firstName || 'User'}
                targetEntityType="user"
                originLocation={{
                  entityType: 'user',
                  entityId: userId,
                  locationType: 'dancefloor',
                  displayName: userTheme.spaceName,
                  prefix: 'at'
                } as OriginLocation}
              />
            </>
          )}

          {activeTab === 'alignments' && userId && (
            <>
              <TabDescriptor
                text={copy.platformPages.userProfile.tabs.alignments.descriptor(user.displayName || user.firstName || 'this user')}
              />
              <AlignmentsGrid userId={userId} />
            </>
          )}

          {activeTab === 'coral' && (
            <>
              <TabDescriptor
                text={copy.platformPages.userProfile.tabs.coral.descriptor(user.displayName || user.firstName || 'this user')}
              />
              <CoralGrid
                corals={detailedCorals}
                isLoading={isCoralsLoading}
                userName={user?.displayName || user?.firstName || 'This user'}
              />
            </>
          )}
        </div>
      </div>
      
      {/* CSS for animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
      ` }} />
    </>
  );
};

export default UserProfilePage;